import React, { useState, useEffect } from "react";
import { MapPin, Save, Shield, Crosshair, Search, Users } from "lucide-react";
import AdminNavbar from "../../components/layout/AdminNavbar";
import api from "../../api/axios";
import { toast } from "react-hot-toast";

// Leaflet Imports
import { MapContainer, TileLayer, Marker, Circle, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import iconUrl from 'leaflet/dist/images/marker-icon.png';
import iconRetinaUrl from 'leaflet/dist/images/marker-icon-2x.png';
import shadowUrl from 'leaflet/dist/images/marker-shadow.png';

// Fix Leaflet's default icon paths in React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl,
    iconUrl,
    shadowUrl
});

const isServiceUnavailableError = (err) =>
    err?.response?.status === 503 || String(err?.message || "").includes("503");

const employeeKey = (employee, index) =>
    employee?._id || employee?.id || employee?.employeeId || employee?.email || `geofence-employee-${index}`;

const MapEventHandler = ({ setLatitude, setLongitude }) => {
    useMapEvents({
        click(e) {
            setLatitude(e.latlng.lat);
            setLongitude(e.latlng.lng);
        },
    });
    return null;
};

const Geofencing = () => {
    const [enabled, setEnabled] = useState(false);
    const [latitude, setLatitude] = useState(12.9716); // Default to Bangalore
    const [longitude, setLongitude] = useState(77.5946);
    const [radius, setRadius] = useState(100); // in meters
    const [, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [mapInstance, setMapInstance] = useState(null);
    const [employees, setEmployees] = useState([]);
    const [employeeSearch, setEmployeeSearch] = useState("");

    useEffect(() => {
        document.title = "Geofencing | Savrion Technologies";
        fetchSettings();
        fetchEmployees();
    }, []);

    const fetchSettings = async () => {
        try {
            const res = await api.get("/api/settings");
            if (res.data.geofence) {
                setEnabled(res.data.geofence.enabled);
                setLatitude(res.data.geofence.latitude);
                setLongitude(res.data.geofence.longitude);
                setRadius(res.data.geofence.radius);
            }
        } catch (err) {
            if (!isServiceUnavailableError(err)) {
                console.error("Failed to fetch settings", err);
                toast.error("Failed to load settings");
            }
        } finally {
            setLoading(false);
        }
    };

    const fetchEmployees = async () => {
        try {
            const res = await api.get("/api/admin/geofence-employees");
            setEmployees(res.data || []);
        } catch (err) {
            if (!isServiceUnavailableError(err)) {
                console.error("Failed to fetch employees", err);
                toast.error("Failed to load employee geofence settings");
            }
        }
    };

    const toggleEmployeeGeofence = async (employeeId) => {
        const previousEmployees = employees;
        const targetEmployee = employees.find((emp) => emp._id === employeeId);
        const nextEnabled = targetEmployee?.geofenceEnabled === false;

        setEmployees((prev) =>
            prev.map((emp) =>
                emp._id === employeeId
                    ? { ...emp, geofenceEnabled: nextEnabled }
                    : emp
            )
        );
        try {
            const res = await api.patch(`/api/admin/employees/${employeeId}/geofence`);
            setEmployees((prev) =>
                prev.map((emp) =>
                    emp._id === employeeId
                        ? { ...emp, geofenceEnabled: res.data.geofenceEnabled }
                        : emp
                )
            );
            toast.success(nextEnabled ? "Geofence enabled" : "Geofence disabled");
        } catch (err) {
            console.error(err);
            setEmployees(previousEmployees);
            toast.error("Failed to update employee geofence setting");
        }
    };

    const filteredEmployees = employees.filter((emp) => {
        const search = employeeSearch.trim().toLowerCase();
        if (!search) return true;
        return [
            emp.name,
            emp.email,
            emp.employeeId,
            emp.team,
            emp.role,
        ].some((value) => String(value || "").toLowerCase().includes(search));
    }).sort((a, b) =>
        String(a.name || "").localeCompare(String(b.name || ""), undefined, { sensitivity: "base" }),
    );

    const handleSave = async () => {
        setSaving(true);
        try {
            await api.post("/api/admin/settings", {
                key: "geofence",
                value: {
                    enabled,
                    latitude,
                    longitude,
                    radius
                }
            });
            toast.success("Geofencing settings updated");
        } catch (err) {
            console.error(err);
            toast.error("Failed to save settings");
        } finally {
            setSaving(false);
        }
    };

    const getCurrentLocation = () => {
        if (!navigator.geolocation) {
            toast.error("Geolocation is not supported by your browser");
            return;
        }

        navigator.geolocation.getCurrentPosition(
            (position) => {
                const lat = position.coords.latitude;
                const lng = position.coords.longitude;
                setLatitude(lat);
                setLongitude(lng);
                
                if (mapInstance) {
                    mapInstance.flyTo([lat, lng], 15);
                }
                
                toast.success("Current location captured");
            },
            () => {
                toast.error("Unable to retrieve your location");
            }
        );
    };

    return (
        <div className="flex flex-col xl:flex-row min-h-screen bg-[var(--bg-primary)] text-[var(--text-primary)]">
            <AdminNavbar />

            <main className="flex-1 min-w-0 px-4 md:px-6 py-8 space-y-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-bold flex items-center gap-3">
                            <MapPin className="text-[var(--accent-primary)]" />
                            Geofencing Settings
                        </h1>
                        <p className="text-[var(--text-secondary)] mt-1">
                            Restrict attendance actions to specific physical locations using OpenStreetMap.
                        </p>
                    </div>

                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="flex items-center justify-center gap-2 px-6 py-3 bg-[var(--accent-primary)] hover:bg-[var(--accent-primary)]/80 text-black font-bold rounded-2xl transition-all disabled:opacity-50"
                    >
                        <Save size={20} />
                        {saving ? "Saving..." : "Save Settings"}
                    </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* CONTROLS */}
                    <div className="lg:col-span-1 space-y-6">
                        <div className="bg-[var(--card-bg)] border border-[var(--border-color)] rounded-3xl p-6 shadow-sm">
                            <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
                                <Shield size={18} className="text-blue-400" />
                                Configuration
                            </h2>

                            <div className="space-y-5">
                                {/* ENABLE TOGGLE */}
                                <div className="flex items-center justify-between p-4 rounded-2xl bg-black/10 dark:bg-[var(--card-bg)]/5 border border-[var(--border-color)]">
                                    <div>
                                        <p className="font-semibold">Enable Geofencing</p>
                                        <p className="text-xs text-[var(--text-secondary)]">Strictly enforce location</p>
                                    </div>
                                    <button
                                        onClick={() => setEnabled(!enabled)}
                                        className={`w-12 h-6 rounded-full transition-colors relative ${enabled ? "bg-emerald-500" : "bg-gray-600"}`}
                                    >
                                        <div className={`absolute top-1 w-4 h-4 rounded-full bg-[var(--card-bg)] transition-all ${enabled ? "left-7" : "left-1"}`} />
                                    </button>
                                </div>

                                {/* RADIUS */}
                                <div>
                                    <label className="block text-sm font-medium mb-2">Radius (meters)</label>
                                    <input
                                        type="number"
                                        value={radius}
                                        onChange={(e) => setRadius(Number(e.target.value))}
                                        className="w-full px-4 py-3 bg-black/10 dark:bg-[var(--card-bg)]/5 border border-[var(--border-color)] rounded-xl outline-none focus:border-[var(--accent-primary)]"
                                        placeholder="e.g. 100"
                                    />
                                </div>

                                {/* COORDS */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium mb-2">Latitude</label>
                                        <input
                                            type="number"
                                            value={latitude}
                                            readOnly
                                            className="w-full px-4 py-3 bg-black/10 dark:bg-[var(--card-bg)]/5 border border-[var(--border-color)] rounded-xl opacity-70"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-2">Longitude</label>
                                        <input
                                            type="number"
                                            value={longitude}
                                            readOnly
                                            className="w-full px-4 py-3 bg-black/10 dark:bg-[var(--card-bg)]/5 border border-[var(--border-color)] rounded-xl opacity-70"
                                        />
                                    </div>
                                </div>

                                <button
                                    onClick={getCurrentLocation}
                                    className="w-full flex items-center justify-center gap-2 py-3 border border-[var(--accent-primary)] text-[var(--accent-primary)] rounded-xl hover:bg-[var(--accent-primary)]/10 transition-all font-medium"
                                >
                                    <Crosshair size={18} />
                                    Use My Current Location
                                </button>
                            </div>
                        </div>

                        <div className="p-5 bg-amber-500/10 border border-amber-500/20 rounded-2xl">
                            <p className="text-sm text-amber-500">
                                <strong>Tip:</strong> Click anywhere on the map to set the office center point. Adjust the radius to cover your entire building.
                            </p>
                        </div>
                    </div>

                    {/* MAP */}
                    <div className="lg:col-span-2 min-h-[500px] h-[500px] bg-[var(--card-bg)] border border-[var(--border-color)] rounded-3xl overflow-hidden shadow-sm relative z-0">
                        <MapContainer
                            center={[latitude, longitude]}
                            zoom={15}
                            style={{ height: "100%", width: "100%" }}
                            ref={setMapInstance}
                        >
                            <TileLayer
                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            />
                            <MapEventHandler setLatitude={setLatitude} setLongitude={setLongitude} />
                            <Marker position={[latitude, longitude]} />
                            <Circle
                                center={[latitude, longitude]}
                                radius={radius}
                                pathOptions={{
                                    color: enabled ? "#10b981" : "#6b7280",
                                    fillColor: enabled ? "#10b981" : "#6b7280",
                                    fillOpacity: 0.35
                                }}
                            />
                        </MapContainer>
                    </div>
                </div>

                <div className="bg-[var(--card-bg)] border border-[var(--border-color)] rounded-3xl p-6 shadow-sm">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-5">
                        <div>
                            <h2 className="text-xl font-bold flex items-center gap-2">
                                <Users size={20} className="text-[var(--accent-primary)]" />
                                Employee Geofence Access
                            </h2>
                            <p className="text-sm text-[var(--text-secondary)] mt-1">
                                Enable or disable geofence enforcement for individual employees.
                            </p>
                        </div>

                        <div className="relative w-full lg:w-80">
                            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
                            <input
                                type="text"
                                value={employeeSearch}
                                onChange={(e) => setEmployeeSearch(e.target.value)}
                                placeholder="Search employee ID, name, team..."
                                className="w-full pl-9 pr-4 py-3 bg-black/10 dark:bg-[var(--card-bg)]/5 border border-[var(--border-color)] rounded-xl outline-none focus:border-[var(--accent-primary)] text-sm"
                            />
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[760px] text-sm">
                            <thead>
                                <tr className="text-left border-b border-[var(--border-color)] text-[var(--text-secondary)]">
                                    <th className="py-3 px-3 font-bold">Employee</th>
                                    <th className="py-3 px-3 font-bold">Employee ID</th>
                                    <th className="py-3 px-3 font-bold">Team</th>
                                    <th className="py-3 px-3 font-bold">Status</th>
                                    <th className="py-3 px-3 font-bold text-right">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredEmployees.map((emp, index) => {
                                    const enabledForEmployee = emp.geofenceEnabled !== false;

                                    return (
                                        <tr key={employeeKey(emp, index)} className="border-b border-[var(--border-color)]/70">
                                            <td className="py-3 px-3">
                                                <div className="font-bold">{emp.name}</div>
                                                <div className="text-xs text-[var(--text-secondary)]">{emp.email}</div>
                                            </td>
                                            <td className="py-3 px-3 font-semibold">{emp.employeeId || "-"}</td>
                                            <td className="py-3 px-3 capitalize">{emp.team || "-"}</td>
                                            <td className="py-3 px-3">
                                                <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${
                                                    enabledForEmployee
                                                        ? "bg-emerald-500/10 text-emerald-500"
                                                        : "bg-[var(--bg-secondary)]0/10 text-gray-400"
                                                }`}>
                                                    {enabledForEmployee ? "Geofence Enabled" : "Geofence Disabled"}
                                                </span>
                                            </td>
                                            <td className="py-3 px-3 text-right">
                                                <button
                                                    type="button"
                                                    onClick={() => toggleEmployeeGeofence(emp._id)}
                                                    className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                                                        enabledForEmployee
                                                            ? "bg-red-500/10 text-red-500 hover:bg-red-500/15"
                                                            : "bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500/15"
                                                    }`}
                                                >
                                                    {enabledForEmployee ? "Disable" : "Enable"}
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>

                        {filteredEmployees.length === 0 && (
                            <div className="py-8 text-center text-[var(--text-secondary)]">
                                No employees found.
                            </div>
                        )}
                    </div>
                </div>
            </main>
        </div>
    );
};

export default Geofencing;


